import { IMessage } from '@color/continuum';
import { Environment, getEnvironment } from '@color/lib';

import {
  IConversationWithMessageIds,
  IZendeskConversation,
  IZendeskMessage,
  MessageSenderRole,
} from './types';

export function convertZendeskMessageToInternalRepresentation(message: IZendeskMessage): IMessage {
  return {
    id: message.id,
    content: message.text ?? '', // Handle case where text might be undefined
    // Date is provided to us in seconds from epoch whereas `new Date` takes milliseconds from epoch.
    sentAt: new Date(message.received * 1000),
    sender:
      message.role === MessageSenderRole.BUSINESS
        ? message.displayName || 'Color Team'
        : message.displayName,
    variant: message.role === MessageSenderRole.USER ? 'outbound' : 'inbound',
  };
}

function addParticipantNameToListIfApplicable(
  message: IZendeskMessage,
  participantList: string[]
): string[] {
  if (
    message.role === MessageSenderRole.BUSINESS &&
    !participantList.includes(message.displayName || 'Color Team')
  ) {
    return [...participantList, message.displayName || 'Color Team'];
  }
  return participantList;
}

export function handleUpdateParticipantListWithNewMessage(
  participantNames: string[],
  displayName: string
) {
  if (!participantNames.includes(displayName)) {
    if (participantNames[0] === 'Care Team') {
      return [displayName];
    }
    return [...participantNames, displayName];
  }
  return participantNames;
}
export function convertZendeskConversationToInternalRepresentation(
  conversation: IZendeskConversation
): IConversationWithMessageIds {
  const computedParticipantList = conversation.messages.reduce(
    (participantList: string[], message: IZendeskMessage) =>
      addParticipantNameToListIfApplicable(message, participantList),
    []
  );

  return {
    id: conversation.id,
    displayName: conversation.displayName,
    isUnreadByPatient: false,
    // The computed participant list may be empty if this is a patient initiated conversation with no response
    // from a care team member. In this scenario, we default to showing "Care Team" (all uses of `participantNames`
    // will call it using `.join(,)` which is why we store it in a list of one element).
    participantNames:
      computedParticipantList.length === 0 ? ['Care Team'] : computedParticipantList,
    latestMessage: convertZendeskMessageToInternalRepresentation(
      conversation.messages
        // Filter out messages that are hidden from patient or have no text content
        // Some messages may not have text (e.g. images)
        .filter((message) => !message.metadata?.is_hidden_from_patient && message.text)
        .at(-1)!
    ),
    messageIds: new Set(conversation.messages.map((message) => message.id)),
  };
}

export function getZendeskIntegrationID() {
  const environment: Environment = getEnvironment();

  if (environment === Environment.PRODUCTION) {
    return process.env.REACT_APP_ZENDESK_INTEGRATION_ID_PRODUCTION;
  }
  if (environment === Environment.STAGING) {
    return process.env.REACT_APP_ZENDESK_INTEGRATION_ID_STAGING;
  }
  return process.env.REACT_APP_ZENDESK_INTEGRATION_ID_LOCAL;
}
